/* */
import React, { useEffect, useCallback, useRef, useMemo, Fragment } from 'react';
import { Input, Table } from 'reactstrap';
import PropTypes from 'prop-types';
import {
  useTable,
  useSortBy,
  usePagination,
  useRowSelect,
  useMountedLayoutEffect,
  useExpanded,
} from 'react-table';
import { MdLastPage, MdFirstPage, MdChevronRight, MdChevronLeft } from 'react-icons/md';
import { suppressNonNumericInput, filterTypeOfValueObj } from 'helpers';
import Loading from 'components/Loading';
import { calculatedPage } from 'helpers/calculate';
import { BsFillInboxFill } from 'react-icons/bs';
import { useTranslation } from 'react-i18next';
import getTheme from 'redux/selector/getTheme';
import { SIZE_ICON, THEME_TYPE } from 'consts/data';
import classNames from 'classnames';
import ScrollWrapper from 'components/ScrollWrapper';
import { CommonIcons } from 'components/Icons';
import { get, isArray } from 'lodash';
import { localeNumber } from 'helpers/string';
import TR from 'components/HtmlTags/TR';
import NewDesign from 'components/NewDesign';
import { cn } from 'helpers/css';

const INITIAL_SELECTED_ROW_IDS = {};

const Sort = ({ column }) => {
  const { isSorted, isSortedDesc } = column || {};

  if (!isSorted) {
    return null;
  }

  return (
    <div
      className='d-inline-flex flex-column '
      style={{
        transform: 'translateY(4px)',
      }}
    >
      <div className='d-flex'>
        {isSortedDesc ? (
          <CommonIcons.SortDown size={SIZE_ICON} className='flex-shrink-0' />
        ) : (
          <CommonIcons.SortUp size={SIZE_ICON} className='flex-shrink-0' />
        )}
        <NewDesign.Typography
          className='flex-grow-1'
          style={{
            fontSize: 8,
            display: 'block',
            height: 'fit-content',
            width: '100%',
            textAlign: 'center',
          }}
        >
          {isSortedDesc ? 'DESC' : 'ASC'}
        </NewDesign.Typography>
      </div>
    </div>
  );
};

const NoDataRow = React.memo(() => {
  const { t } = useTranslation();

  return (
    <div className='table__no-data-row'>
      <BsFillInboxFill />
      {t('shared:data_not_found')}
    </div>
  );
});

const LoadingTable = React.memo(() => {
  return (
    <div className='table__loading'>
      <Loading size='md' />
    </div>
  );
});

const Pagination = React.memo((props) => {
  const {
    pageSize,
    pageIndex,
    pageCount,
    goToPageRef,
    setPageSizeControlled,
    handleBlur,
    handleKeyDownPress,
    goToPageControlled,
    previousPageControlled,
    nextPageControlled,
    canPreviousPageControlled,
    canNextPageControlled,
    disablePagination,
    t,
  } = props;
  if (disablePagination) {
    return null;
  }
  return (
    <div className='pagination'>
      <div className='pagination-rowperpage'>
        <span>{t('shared:rows_per_page')}: </span>
        <Input
          style={{ display: 'inline-block', minWidth: 78 }}
          type='select'
          value={pageSize}
          onChange={(e) => setPageSizeControlled(Number(e.target.value))}
        >
          {[10, 20, 30, 50, 100, 500, 1000].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              {pageSize}
            </option>
          ))}
        </Input>
      </div>
      <div className='pagination-page'>
        <span
          dangerouslySetInnerHTML={{
            __html: t('shared:page_of', {
              pageIndex: pageIndex + 1,
              pageCount: pageCount,
            }),
          }}
        />
        <span>
          | {t('shared:go_to_page')}:{' '}
          <Input
            type='text'
            max={pageCount}
            defaultValue={pageIndex + 1}
            disabled={pageCount <= 1}
            innerRef={goToPageRef}
            name='gotopage'
            onBlur={handleBlur}
            onKeyDown={handleKeyDownPress}
            style={{ width: '62px', display: 'inline-block' }}
          />
        </span>{' '}
      </div>
      <div className='pagination-switch'>
        <button
          className='btn'
          onClick={() => goToPageControlled(0)}
          disabled={!canPreviousPageControlled}
        >
          <MdFirstPage />
        </button>{' '}
        <button
          className='btn'
          onClick={() => previousPageControlled()}
          disabled={!canPreviousPageControlled}
        >
          <MdChevronLeft />
        </button>{' '}
        <button
          className='btn'
          onClick={() => nextPageControlled()}
          disabled={!canNextPageControlled}
        >
          <MdChevronRight />
        </button>{' '}
        <button
          className='btn'
          onClick={() => goToPageControlled(pageCount - 1)}
          disabled={!canNextPageControlled}
        >
          <MdLastPage />
        </button>{' '}
      </div>
    </div>
  );
});

const IndeterminateCheckbox = React.forwardRef(({ indeterminate, ...rest }, ref) => {
  const defaultRef = React.useRef();
  const resolvedRef = ref || defaultRef;

  React.useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  return (
    <>
      <input type='checkbox' ref={resolvedRef} {...rest} />
    </>
  );
});

const ReactTable = (props) => {
  // Use the state and functions returned from useTable to build your UI
  const {
    styleTable,
    columns,
    data,
    width,
    filters,
    sortBy: controlledSortBy,
    toggleSort,
    handleSelectAllRecord,
    isLoading,
    isSelectAllRecord,
    setSelectedRow,
    disablePagination,
    disableSortBy,
    disabledSelectRow,
    onChangeStart = () => {},

    enableSelectAllRecord,
    t,
    fixedColumn,
    expandRows,
  } = props;
  const theme = getTheme();
  const goToPageRef = useRef({});

  const { pageCount: controlledPageCount } = filters;
  const controlledPageIndex = filters?.page || filters?.pageIndex || 0;
  const controlledPageSize = filters?.pageSize || filters?.size;

  const getRowIdByKey = !!props.primaryKey
    ? useCallback((row) => {
        return get(row, props.primaryKey);
      }, [])
    : props?.getRowId;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    pageCount,
    isAllRowsSelected,
    selectedFlatRows,
    toggleAllRowsSelected,
    visibleColumns,
    state: { pageIndex, pageSize, selectedRowIds },
  } = useTable(
    {
      columns,
      data,
      externalMethod: filterTypeOfValueObj(props),
      initialState: React.useMemo(() => {
        return {
          selectedRowIds: INITIAL_SELECTED_ROW_IDS,
          // expandRows: { primaryKey: true }
          expanded: expandRows || {},
        };
      }, []),
      useControlledState: (state) => {
        return React.useMemo(() => {
          return {
            ...state,
            pageCount: calculatedPage({
              total: props.total || data.length,
              itemPerPage: controlledPageSize,
            }),
            pageSize: controlledPageSize,
            pageIndex: controlledPageIndex,
            sortBy: controlledSortBy,
          };
        }, [state, controlledPageIndex, controlledSortBy, controlledPageSize, controlledPageCount]);
      },
      pageSize: controlledPageSize,
      pageCount: calculatedPage({
        total: props.total || data.length,
        itemPerPage: controlledPageSize,
      }),
      manualPagination: true,
      manualSortBy: true,
      manualRowSelectedKey: true,
      autoResetSortBy: false,
      autoResetPage: false,
      autoResetExpanded: props.autoResetExpanded || false,
      autoResetSelectedRows: true,
      autoResetRowState: false,
      getRowId: getRowIdByKey,
    },
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      if (!disabledSelectRow) {
        hooks.visibleColumns.push((columns) => [
          // Let's make a column for selection
          {
            id: 'selection',
            // The header can use the table's getToggleAllRowsSelectedProps method
            // to render a checkbox
            Header: (propsHeader) => (
              <div>
                <IndeterminateCheckbox {...propsHeader.getToggleAllRowsSelectedProps()} />
              </div>
            ),
            // The cell can use the individual row's getToggleRowSelectedProps method
            // to the render a checkbox
            Cell: ({ row }) => (
              <div>
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </div>
            ),
          },
          ...columns,
        ]);
      }
    }
  );

  // Update Selected Flat
  useMountedLayoutEffect(() => {
    setSelectedRow(selectedFlatRows);
  }, [selectedRowIds]);

  // Update Value of Go-to-Page Input
  useEffect(() => {
    goToPageRef.current.value = controlledPageIndex + 1;
  }, [controlledPageIndex]);

  // Function Controlled
  const goToPageControlled = useCallback(
    (pageIndex) => {
      onChangeStart();
      props.handleChangePage && props.handleChangePage(pageIndex);
    },
    [controlledPageIndex, filters]
  );

  const previousPageControlled = useCallback(() => {
    goToPageControlled(controlledPageIndex - 1);
  }, [controlledPageIndex, filters]);

  const nextPageControlled = useCallback(() => {
    goToPageControlled(controlledPageIndex + 1);
  }, [controlledPageIndex, filters]);

  const canNextPageControlled = controlledPageIndex + 1 < pageCount;
  const canPreviousPageControlled = controlledPageIndex > 0;

  const setPageSizeControlled = useCallback(
    (pageSize) => {
      onChangeStart();
      props.handleChangePageSize && props.handleChangePageSize(pageSize);
    },
    [controlledPageSize, filters]
  );

  const handleBlur = useCallback(
    (e) => {
      const { value, name } = e.target;
      if (name === 'gotopage') {
        if (value) {
          let outputValue;
          if (Number(value) > pageCount) {
            outputValue = pageCount;
          } else if (Number(value) > 0) {
            outputValue = Number(value);
          } else {
            outputValue = 0;
          }
          goToPageRef.current.value = outputValue;
          goToPageControlled(outputValue - 1);
        }
      }
    },
    [goToPageRef.current, pageCount]
  );

  const handleKeyDownPress = useCallback(
    (e) => {
      suppressNonNumericInput(e);
      const { value, name } = e.target;
      const key = e.which || e.keyCode;
      // Button Enter
      if (key === 13) {
        if (name === 'gotopage') {
          if (value) {
            let outputValue;
            if (Number(value) > pageCount) {
              outputValue = pageCount;
            } else if (Number(value) > 0) {
              outputValue = Number(value);
            } else {
              outputValue = 0;
            }
            goToPageRef.current.blur();
            goToPageRef.current.value = outputValue;
            goToPageControlled(outputValue - 1);
          }
        }
      }
    },
    [goToPageRef.current, pageCount]
  );

  const onScrollEnd = useCallback(() => {
    if (props.isLoadingMore || props.isLoading) {
      return;
    }

    if (props.hasMore) {
      props.fetchMore && props.fetchMore();
    }
  }, [props.isLoading, props.isLoadingMore, props.hasMore, props.fetchMore]);
  // End Function

  //! Render the UI for your table
  const Wrapper = props.isInfiniteLoader ? ScrollWrapper : Fragment;
  const propsWrapper = props.isInfiniteLoader
    ? {
        onScrollEnd,
        marginTop: -250,
      }
    : {};

  const TrComponent = props.trComponent ?? TR;

  return (
    <div
      className={classNames('table-root', props.classNameRoot, {
        'material-table': props.isMaterial || true,
        'fixed-column': fixedColumn,
      })}
    >
      <div
        className={cn('tableWrap', props.classTableWrap)}
        style={{ maxHeight: props.maxHeight, minHeight: props.minHeight, ...props.styleTableWrap }}
      >
        <Table
          hover
          // responsive
          {...getTableProps()}
          className='table-outline mb-0 d-sm-table'
          style={{ minWidth: width, width: '100%', ...(styleTable || {}) }}
        >
          {!props.hideHeader && (
            <thead className={theme === THEME_TYPE.dark ? 'thead-dark' : 'thead-light'}>
              {!disabledSelectRow && enableSelectAllRecord && (
                <tr className='row-select-all'>
                  <td>
                    <Input
                      type='checkbox'
                      className='checkbox-form-relative'
                      style={{ marginLeft: 0 }}
                      onClick={() => {
                        !isAllRowsSelected && toggleAllRowsSelected();
                        isAllRowsSelected && isSelectAllRecord && toggleAllRowsSelected();
                      }}
                      onChange={(e) => {
                        handleSelectAllRecord && handleSelectAllRecord(e.target.checked);
                      }}
                      checked={isSelectAllRecord}
                    />
                  </td>
                  <td>{t('shared:select_all')}</td>
                  <td>
                    {t('shared:select_items')}:{' '}
                    {isSelectAllRecord ? localeNumber(props?.total) : selectedFlatRows.length}
                  </td>
                </tr>
              )}
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => {
                    const isSortable = !disableSortBy && !column.disableSort;
                    const isSorting = column?.isSorted;

                    return (
                      <th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        className={
                          column.disableSort || disableSortBy ? 'disable-sort' : 'enable-sort'
                        }
                        style={column?.styleCol}
                        onClick={
                          column.id !== 'selection' && !column.disableSort && !disableSortBy
                            ? toggleSort(column.id, {
                                isSortDesc: column.isSortedDesc,
                                setToFilters: true,
                              })
                            : undefined
                        }
                        title=''
                      >
                        <div
                          style={{
                            width: 'max-content',
                            color: isSorting ? 'var(--color-primary-v2)' : undefined,
                          }}
                        >
                          {column.render('Header')}
                          {isSortable && <Sort column={column} />}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
          )}
          {/* Check loading -> data.length -> render data */}
          <Wrapper {...propsWrapper}>
            <tbody {...getTableBodyProps()}>
              {!isLoading &&
                data.length > 0 &&
                rows.map((row, idxRow) => {
                  prepareRow(row);

                  const { role, ...rest } = row.getRowProps();
                  const keyOfEachRow = get(row, `original.${props?.primaryKey}`);

                  return (
                    <Fragment key={props?.primaryKey ? `${keyOfEachRow}` : rest.key}>
                      <TrComponent className='hover-table' row={row}>
                        {row.cells.map((cell, idxCol) => {
                          return (
                            <td
                              {...cell.getCellProps()}
                              className={classNames(
                                props.disableCellGrowEqually ? 'disabled-grow-cell' : '',
                                cell?.column?.classNameCell
                              )}
                              style={cell?.column?.styleCell}
                              key={
                                props?.primaryKey
                                  ? `${keyOfEachRow}-${idxCol}`
                                  : `row-${idxRow}-col-${idxCol}-${
                                      cell?.row?.[props?.primaryKey || ''] || ''
                                    }`
                              }
                            >
                              {cell.render('Cell')}
                            </td>
                          );
                        })}
                      </TrComponent>

                      {row.isExpanded ? (
                        <tr>
                          <td colSpan={visibleColumns.length}>
                            {/*
                          Inside it, call our renderRowSubComponent function. In reality,
                          you could pass whatever you want as props to
                          a component like this, including the entire
                          table instance. But for this example, we'll just
                          pass the row
                        */}
                            {props?.renderRowSubComponent && props.renderRowSubComponent({ row })}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
            </tbody>
          </Wrapper>
        </Table>

        {(isLoading || props.isLoadingMore) && <LoadingTable />}
        {!isLoading && data.length === 0 && <NoDataRow />}
      </div>

      <Pagination
        disablePagination={disablePagination}
        pageSize={pageSize}
        pageIndex={pageIndex}
        pageCount={pageCount}
        goToPageRef={goToPageRef}
        setPageSizeControlled={setPageSizeControlled}
        handleBlur={handleBlur}
        handleKeyDownPress={handleKeyDownPress}
        goToPageControlled={goToPageControlled}
        previousPageControlled={previousPageControlled}
        nextPageControlled={nextPageControlled}
        canPreviousPageControlled={canPreviousPageControlled}
        canNextPageControlled={canNextPageControlled}
        t={t}
      />
    </div>
  );
};

ReactTable.defaultProps = {
  width: 2000,
  disablePagination: false,
  disableSortBy: false,
  disabledSelectRow: false,
  styleTable: {},
};

const propTypes = {
  filters: PropTypes.object,
  sortBy: PropTypes.array,
  setFilters: PropTypes.func,
  setSelectedFlatRows: PropTypes.func,
  toggleSort: PropTypes.func,
  handleChangePage: PropTypes.func,
  handleSelectAllRecord: PropTypes.func,
  handleChangePageSize: PropTypes.func,

  disablePagination: PropTypes.bool,
  disableSortBy: PropTypes.bool,
  disabledSelectRow: PropTypes.bool,
  isLoading: PropTypes.bool,
  enableSelectAllRecord: PropTypes.bool,
  disableCellGrowEqually: PropTypes.bool,
  autoResetExpanded: PropTypes.bool,

  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  styleTable: PropTypes.object,
  styleTableWrap: PropTypes.object,

  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  total: PropTypes.number,
  fixedColumn: PropTypes.bool,
  hideHeader: PropTypes.bool,

  //* Props for loadmore
  isInfiniteLoader: PropTypes.bool,
  fetchMore: PropTypes.func,
  hasMore: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  primaryKey: PropTypes.string,
  maxHeight: PropTypes.string,

  trComponent: PropTypes.node,
  classNameRoot: PropTypes.string,
  classTableWrap: PropTypes.string,
};

const TableContainer = (props) => {
  const {
    filters = [],
    sortBy = [],
    setSelectedFlatRows,
    toggleSort = () => {},
    isSelectAllRecord = false,
    handleSelectAllRecord,
    handleChangePage,
    handleChangePageSize,

    enableSelectAllRecord = false,
    disabledSelectRow = false,
    disableSortBy = false,
    disablePagination = false,
  } = props;
  const { t } = useTranslation();

  // Memorize
  const data = useMemo(() => {
    return isArray(props?.data) ? props?.data : [];
  }, [props.data]);

  const columns = useMemo(() => {
    return props?.columns || [];
  }, [props.columns]);

  const total = useMemo(() => {
    return props.total;
  }, [props.total]);

  return (
    <ReactTable
      {...props}
      data={data}
      columns={columns}
      total={total}
      filters={filters}
      setSelectedRow={setSelectedFlatRows}
      sortBy={sortBy}
      toggleSort={toggleSort}
      isSelectAllRecord={isSelectAllRecord}
      handleSelectAllRecord={handleSelectAllRecord}
      handleChangePage={handleChangePage}
      handleChangePageSize={handleChangePageSize}
      enableSelectAllRecord={enableSelectAllRecord}
      disablePagination={disablePagination}
      disableSortBy={disableSortBy}
      disabledSelectRow={disabledSelectRow}
      t={t}
    />
  );
};

TableContainer.propTypes = propTypes;
export default React.memo(TableContainer);
